// Rental cross-sell on accommodation product pages.
//
// Reads the event id and plan type off the product's own tags (never a Shopify
// tag SEARCH — it tokenises on hyphens and over-matches), asks the admin API
// which rentals are offered for that plan type right now, and renders a quantity
// row per item. `product-form.js` picks the selections up through
// `window.FG_RENTALS.collectCartItems()` and sends them in the same
// /cart/add.js call as the plan itself.
//
// DELIBERATELY NEVER TOUCHES THE BUY BUTTON. product-form.liquid is fail-closed
// because a booking made with an unrendered form is unrepairable; a rentals
// fetch that fails only costs an upsell. It would also race that block's
// `captureExternal()` snapshot. On any error this component hides itself.

import { eventIdFromTags, planTypeFromTags } from '@fujigravel/api-contract'

const YEN = (n) => `¥${Number(n).toLocaleString('ja-JP')}`

class AccommodationRentalsComponent extends HTMLElement {
    #offerings = []

    connectedCallback() {
        this.#load().catch((err) => {
            console.warn('[accommodation-rentals]', err)
            this.hidden = true
        })
    }

    async #load() {
        const apiBase = (this.dataset.apiBase || '').replace(/\/$/, '')
        const tags = (this.dataset.productTags || '').split(',').map((t) => t.trim()).filter(Boolean)
        const eventId = eventIdFromTags(tags)
        const planType = planTypeFromTags(tags)
        if (!apiBase || !eventId) return

        const url = `${apiBase}/accommodation/rentals?event_id=${encodeURIComponent(eventId)}`
            + (planType ? `&plan_type=${encodeURIComponent(planType)}` : '')
        const res = await fetch(url, { headers: { Accept: 'application/json' } })
        if (!res.ok) throw new Error(`rentals fetch ${res.status}`)
        const data = await res.json()

        // Only items the server says are purchasable AND that we can actually add
        // to a cart (a variant id). Anything else is silently omitted rather than
        // rendered as a broken row.
        this.#offerings = (data.offerings || []).filter((o) => o.variant_id)
        if (!this.#offerings.length) return

        this.#render()
        this.hidden = false
    }

    #render() {
        const list = this.querySelector('[data-ref="list"]')
        if (!list) return
        list.replaceChildren(...this.#offerings.map((o) => this.#row(o)))
        this.#updateSubtotal()
    }

    #row(offering) {
        const row = document.createElement('div')
        row.className = 'accommodation-rentals__row'

        const label = document.createElement('span')
        label.className = 'accommodation-rentals__label'
        label.textContent = offering.label
        if (offering.description) {
            const note = document.createElement('span')
            note.className = 'accommodation-rentals__note'
            note.textContent = offering.description
            label.appendChild(document.createElement('br'))
            label.appendChild(note)
        }

        const price = document.createElement('span')
        price.className = 'accommodation-rentals__price'
        price.textContent = YEN(offering.unit_price_jpy)

        const qty = document.createElement('input')
        qty.type = 'number'
        qty.className = 'accommodation-rentals__qty'
        qty.min = '0'
        qty.step = '1'
        qty.value = '0'
        qty.inputMode = 'numeric'
        qty.setAttribute('aria-label', `${offering.label} の数量`)
        if (offering.max_per_order) qty.max = String(offering.max_per_order)
        // No `name` and no `form` attribute: these inputs must NOT be submitted
        // as line-item properties of the accommodation product. They become
        // separate cart line items instead (see collectCartItems below).
        qty.dataset.variantId = String(offering.variant_id)
        // Price rides on the input too, so the subtotal never depends on the row
        // order matching #offerings by index.
        qty.dataset.unitPrice = String(offering.unit_price_jpy)
        qty.addEventListener('input', () => this.#updateSubtotal())

        row.append(label, price, qty)
        return row
    }

    #updateSubtotal() {
        const el = this.querySelector('[data-ref="subtotal"]')
        if (!el) return
        const total = this.#selections().reduce((sum, s) => sum + s.quantity * s.unit_price_jpy, 0)
        el.textContent = total > 0 ? `レンタル小計 ${YEN(total)}` : ''
        el.hidden = total <= 0
    }

    #selections() {
        return [...this.querySelectorAll('.accommodation-rentals__qty')]
            .map((input) => ({
                quantity: Math.max(0, Number(input.value) || 0),
                unit_price_jpy: Number(input.dataset.unitPrice) || 0,
                variant_id: Number(input.dataset.variantId),
            }))
            .filter((s) => s.quantity > 0 && s.variant_id)
    }

    /** Cart line items for the current selection, in /cart/add.js `items` shape. */
    cartItems() {
        const parentHandle = this.dataset.productHandle || ''
        return this.#selections().map((s) => ({
            id: s.variant_id,
            quantity: s.quantity,
            // Underscore-prefixed: Shopify hides these from the customer in
            // cart/checkout, and the sync's translateAttributes skips them, so
            // they never pollute an answer set. `_parent_plan` is the strongest
            // linkage signal the sync has.
            properties: { _parent_plan: parentHandle },
        }))
    }
}

if (!customElements.get('accommodation-rentals-component')) {
    customElements.define('accommodation-rentals-component', AccommodationRentalsComponent)
}

// The contract product-form.js consumes. Returns [] on every page that has no
// rentals block, which is what keeps the shared add-to-cart path unchanged
// everywhere else in the store.
window.FG_RENTALS = {
    collectCartItems() {
        const el = document.querySelector('accommodation-rentals-component')
        if (!el || el.hidden || typeof el.cartItems !== 'function') return []
        return el.cartItems()
    },
}
